import { useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import Link from '@mui/material/Link';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  MyOnboardingTaskSchema,
  OnboardingSchema,
  OnboardingTemplateSchema,
  type EmployeeDto,
  type OnboardingDto,
  type OnboardingTaskDto,
  type OnboardingTemplateDto,
} from '@taqon/contracts';
import { Link as RouterLink } from 'react-router';
import { z } from 'zod';
import { getData, patchJson, postJson } from '../../shared/api/client';
import { hasCapability, useSession } from '../../shared/session/session';
import { ConfirmDialog } from '../../shared/ui/confirm';
import { FormDialog, StatusChip } from '../../shared/ui/forms';
import { EmptyState, ErrorState, LoadingState, PageHeader, Section } from '../../shared/ui/page';
import { PersonName } from '../../shared/ui/person';
import { EmployeePicker } from '../finance/lookups';
import { messageOf } from './leave';
import { useFilterEmployee } from './url-filters';

/**
 * Staff onboarding (CLR-48; FRD-HRP-099 to HRP-104): HR's templates, HR's overview of
 * checklists, the checklist on an employee record, and the tasks assigned to the signed-in
 * employee. The API decides who sees and ticks what; the page offers only what it says.
 */
const HR = '/api/v1/hr';
const TEMPLATES = `${HR}/onboarding-templates`;

const onboardingKey = (employeeId: string) => ['hr', 'onboarding', 'employee', employeeId];

async function invalidateOnboarding(queryClient: ReturnType<typeof useQueryClient>) {
  await queryClient.invalidateQueries({ queryKey: ['hr', 'onboarding'] });
}

function useTemplates(enabled = true) {
  return useQuery({
    queryKey: ['hr', 'onboarding', 'templates'],
    enabled,
    queryFn: () => getData(TEMPLATES, z.array(OnboardingTemplateSchema)),
  });
}

/** "2 of 3 tasks done", with a bar. */
function Progress({ done, count }: { done: number; count: number }) {
  return (
    <Stack spacing={0.5} sx={{ minWidth: 140 }}>
      <Typography variant="body2">
        {done} of {count} tasks done
      </Typography>
      <LinearProgress
        variant="determinate"
        value={count === 0 ? 0 : (done / count) * 100}
        aria-label={`${String(done)} of ${String(count)} tasks done`}
      />
    </Stack>
  );
}

/** Ticks or unticks a task (`If-Match`). */
function useTick() {
  const session = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (task: OnboardingTaskDto) =>
      postJson(
        `${HR}/onboarding-tasks/${task.id}/tick`,
        { done: !task.done },
        OnboardingSchema,
        session.data?.csrfToken ?? '',
        { expectedVersion: task.version },
      ),
    onSuccess: async () => {
      await invalidateOnboarding(queryClient);
    },
  });
}

function DoneBox({
  task,
  onTick,
  busy,
}: {
  task: OnboardingTaskDto;
  onTick: () => void;
  busy: boolean;
}) {
  return (
    <Checkbox
      checked={task.done}
      disabled={!task.canTick || busy}
      onChange={onTick}
      slotProps={{ input: { 'aria-label': `${task.done ? 'Reopen' : 'Done'}: ${task.title}` } }}
    />
  );
}

// ------------------------------------------------------------------ Employee record

/** The employee's onboarding checklist on their record; HR starts it from a template. */
export function OnboardingSection({
  employee,
  canStart,
}: {
  employee: EmployeeDto;
  canStart: boolean;
}) {
  const session = useSession();
  const queryClient = useQueryClient();
  const checklist = useQuery({
    queryKey: onboardingKey(employee.id),
    retry: false,
    queryFn: () =>
      getData(`${HR}/employees/${employee.id}/onboarding`, OnboardingSchema.nullable()),
  });
  const mayStart = canStart && hasCapability(session.data, 'hr.onboarding:create');
  const templates = useTemplates(mayStart && checklist.data === null);
  const [templateId, setTemplateId] = useState('');
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState<OnboardingTaskDto | null>(null);
  const tick = useTick();
  const start = useMutation({
    mutationFn: () =>
      postJson(
        `${HR}/employees/${employee.id}/onboarding`,
        { templateId },
        OnboardingSchema,
        session.data?.csrfToken ?? '',
      ),
    onSuccess: async () => {
      await invalidateOnboarding(queryClient);
    },
  });

  if (checklist.isPending || checklist.isError) return null;
  const onboarding = checklist.data;
  if (onboarding === null && !(mayStart && employee.status === 'active')) return null;

  return (
    <Section
      title="Onboarding"
      actions={
        onboarding?.canManage === true && onboarding.status === 'open' ? (
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={(event) => {
              event.currentTarget.focus();
              setAdding(true);
            }}
          >
            Add task
          </Button>
        ) : undefined
      }
    >
      <Stack spacing={2} sx={{ p: 2 }}>
        {onboarding === null ? (
          <>
            <Typography variant="body2" color="text.secondary">
              No onboarding checklist yet. Start one from a template: each task is given to its
              responsible person, due a number of days after the joining date.
            </Typography>
            {templates.data !== undefined && templates.data.every((t) => t.status !== 'active') && (
              <Alert severity="info" variant="outlined">
                No active onboarding template yet. HR creates one under HR › Onboarding templates.
              </Alert>
            )}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                select
                size="small"
                label="Template"
                value={templateId}
                onChange={(e) => {
                  setTemplateId(e.target.value);
                }}
                sx={{ minWidth: 260 }}
              >
                {(templates.data ?? [])
                  .filter((t) => t.status === 'active')
                  .map((t) => (
                    <MenuItem key={t.id} value={t.id}>
                      {t.name} ({t.tasks.length} tasks)
                    </MenuItem>
                  ))}
              </TextField>
              <Button
                variant="contained"
                disabled={templateId === '' || start.isPending}
                onClick={() => {
                  start.mutate();
                }}
              >
                Start onboarding
              </Button>
            </Stack>
            {start.isError && <ErrorState message={messageOf(start.error)} />}
          </>
        ) : (
          <>
            <Stack direction="row" spacing={2} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
              <StatusChip status={onboarding.status} />
              <Progress done={onboarding.tasksDone} count={onboarding.taskCount} />
              {onboarding.templateName !== null && (
                <Typography variant="body2" color="text.secondary">
                  From the template {onboarding.templateName}
                </Typography>
              )}
            </Stack>
            {tick.isError && <ErrorState message={messageOf(tick.error)} />}
            <Table size="small" aria-label="Onboarding tasks">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">Done</TableCell>
                  <TableCell>Task</TableCell>
                  <TableCell>Responsible</TableCell>
                  <TableCell>Due</TableCell>
                  <TableCell>Completed</TableCell>
                  {onboarding.canManage && <TableCell align="right">Action</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {onboarding.tasks.map((task) => (
                  <TableRow key={task.id}>
                    <TableCell padding="checkbox">
                      <DoneBox
                        task={task}
                        busy={tick.isPending}
                        onTick={() => {
                          tick.mutate(task);
                        }}
                      />
                    </TableCell>
                    <TableCell>{task.title}</TableCell>
                    <TableCell>
                      {task.responsibleName === null ? (
                        '—'
                      ) : (
                        <PersonName
                          name={task.responsibleName}
                          employeeId={task.responsibleEmployeeId}
                        />
                      )}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{task.dueDate}</TableCell>
                    <TableCell>
                      {task.doneAt === null ? (
                        '—'
                      ) : task.doneByName === null ? (
                        new Date(task.doneAt).toLocaleDateString('en-GB')
                      ) : (
                        <PersonName name={task.doneByName} userId={task.doneBy}>
                          {new Date(task.doneAt).toLocaleDateString('en-GB')}, {task.doneByName}
                        </PersonName>
                      )}
                    </TableCell>
                    {onboarding.canManage && (
                      <TableCell align="right">
                        {!task.done && (
                          <IconButton
                            size="small"
                            aria-label={`Remove ${task.title}`}
                            onClick={(event) => {
                              event.currentTarget.focus();
                              setRemoving(task);
                            }}
                          >
                            <DeleteOutlinedIcon fontSize="small" />
                          </IconButton>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}
      </Stack>
      {adding && onboarding !== null && (
        <AddTaskDialog
          onboarding={onboarding}
          onClose={() => {
            setAdding(false);
          }}
        />
      )}
      {removing !== null && (
        <RemoveTask
          task={removing}
          onClose={() => {
            setRemoving(null);
          }}
        />
      )}
    </Section>
  );
}

function AddTaskDialog({
  onboarding,
  onClose,
}: {
  onboarding: OnboardingDto;
  onClose: () => void;
}) {
  const session = useSession();
  const queryClient = useQueryClient();
  const [title, setTitle] = useState('');
  const [responsible, setResponsible] = useState<EmployeeDto | null>(null);
  const [dueDate, setDueDate] = useState('');
  const save = useMutation({
    mutationFn: () =>
      postJson(
        `${HR}/onboardings/${onboarding.id}/tasks`,
        { title: title.trim(), responsibleEmployeeId: responsible?.id ?? '', dueDate },
        OnboardingSchema,
        session.data?.csrfToken ?? '',
      ),
    onSuccess: async () => {
      await invalidateOnboarding(queryClient);
      onClose();
    },
  });
  return (
    <FormDialog
      open
      title="Add an onboarding task"
      submitLabel="Add task"
      canSubmit={title.trim() !== '' && responsible !== null && dueDate !== ''}
      submitting={save.isPending}
      error={save.isError ? messageOf(save.error) : null}
      onClose={onClose}
      onSubmit={() => {
        save.mutate();
      }}
    >
      <TextField
        required
        label="Task"
        value={title}
        onChange={(e) => {
          setTitle(e.target.value);
        }}
        slotProps={{ htmlInput: { maxLength: 200 } }}
      />
      <EmployeePicker label="Responsible" required value={responsible} onChange={setResponsible} />
      <TextField
        required
        type="date"
        label="Due date"
        value={dueDate}
        onChange={(e) => {
          setDueDate(e.target.value);
        }}
        slotProps={{ inputLabel: { shrink: true } }}
      />
    </FormDialog>
  );
}

function RemoveTask({ task, onClose }: { task: OnboardingTaskDto; onClose: () => void }) {
  const session = useSession();
  const queryClient = useQueryClient();
  const remove = useMutation({
    mutationFn: () =>
      postJson(
        `${HR}/onboarding-tasks/${task.id}/remove`,
        {},
        OnboardingSchema,
        session.data?.csrfToken ?? '',
        { expectedVersion: task.version },
      ),
    onSuccess: async () => {
      await invalidateOnboarding(queryClient);
      onClose();
    },
  });
  return (
    <ConfirmDialog
      open
      title={`Remove ${task.title}?`}
      message="It no longer counts towards the checklist. It stays in the record's history."
      confirmLabel="Remove"
      tone="warning"
      busy={remove.isPending}
      error={remove.isError ? messageOf(remove.error) : null}
      onCancel={onClose}
      onConfirm={() => {
        remove.mutate();
      }}
    />
  );
}

// ------------------------------------------------------------------ My tasks

/** The onboarding tasks assigned to the signed-in employee (FRD-HRP-102). */
export function MyOnboardingTasksPage() {
  const tasks = useQuery({
    queryKey: ['hr', 'onboarding', 'my-tasks'],
    queryFn: () => getData(`${HR}/onboarding/my-tasks`, z.array(MyOnboardingTaskSchema)),
  });
  const tick = useTick();
  const rows = tasks.data ?? [];
  return (
    <Stack spacing={3}>
      <PageHeader
        title="My onboarding tasks"
        description="Tasks you are responsible for in the onboarding of new colleagues."
      />
      {tick.isError && <ErrorState message={messageOf(tick.error)} />}
      <Section>
        {tasks.isPending && <LoadingState label="Loading your onboarding tasks" />}
        {tasks.isError && <ErrorState message={tasks.error.message} />}
        {tasks.data !== undefined && rows.length === 0 && (
          <EmptyState
            title="No onboarding tasks"
            detail="Tasks appear here when HR makes you responsible for one."
          />
        )}
        {rows.length > 0 && (
          <Table size="small" aria-label="My onboarding tasks">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">Done</TableCell>
                <TableCell>Task</TableCell>
                <TableCell>New colleague</TableCell>
                <TableCell>Due</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((task) => (
                <TableRow key={task.id}>
                  <TableCell padding="checkbox">
                    <DoneBox
                      task={task}
                      busy={tick.isPending}
                      onTick={() => {
                        tick.mutate(task);
                      }}
                    />
                  </TableCell>
                  <TableCell>{task.title}</TableCell>
                  <TableCell>
                    <PersonName name={task.employeeName} employeeId={task.employeeId}>
                      {task.employeeCode} — {task.employeeName}
                    </PersonName>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{task.dueDate}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Section>
    </Stack>
  );
}

// ------------------------------------------------------------------ HR overview

/** HR's overview of checklists, open or completed, each linked to the employee's record. */
export function OnboardingPage() {
  const [status, setStatus] = useState<'open' | 'completed'>('open');
  const list = useQuery({
    queryKey: ['hr', 'onboarding', 'list', status],
    queryFn: () => getData(`${HR}/onboardings?status=${status}`, z.array(OnboardingSchema)),
  });
  const rows = list.data ?? [];
  return (
    <Stack spacing={3}>
      <PageHeader
        title="Onboarding"
        description="Onboarding checklists of new employees. Start one from the employee's record."
      />
      <Section
        title={status === 'open' ? 'In progress' : 'Completed'}
        actions={
          <TextField
            select
            size="small"
            label="Status"
            value={status}
            onChange={(e) => {
              setStatus(e.target.value === 'completed' ? 'completed' : 'open');
            }}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="open">In progress</MenuItem>
            <MenuItem value="completed">Completed</MenuItem>
          </TextField>
        }
      >
        {list.isPending && <LoadingState label="Loading onboarding checklists" />}
        {list.isError && <ErrorState message={list.error.message} />}
        {list.data !== undefined && rows.length === 0 && (
          <EmptyState
            title={status === 'open' ? 'No onboarding in progress' : 'None completed yet'}
          />
        )}
        {rows.length > 0 && (
          <Table size="small" aria-label="Onboarding checklists">
            <TableHead>
              <TableRow>
                <TableCell>Employee</TableCell>
                <TableCell>Template</TableCell>
                <TableCell>Progress</TableCell>
                <TableCell>Started</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>
                    <PersonName name={row.employeeName} employeeId={row.employeeId}>
                      <Link component={RouterLink} to={`/hr/employees/${row.employeeId}`}>
                        {row.employeeCode} — {row.employeeName}
                      </Link>
                    </PersonName>
                  </TableCell>
                  <TableCell>{row.templateName ?? '—'}</TableCell>
                  <TableCell>
                    <Progress done={row.tasksDone} count={row.taskCount} />
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {new Date(row.startedAt).toLocaleDateString('en-GB')}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Section>
    </Stack>
  );
}

// ------------------------------------------------------------------ Templates

/** HR's onboarding templates: the tasks a checklist starts with (FRD-HRP-099). */
export function OnboardingTemplatesPage() {
  const session = useSession();
  const canCreate = hasCapability(session.data, 'hr.onboarding_template:create');
  const canChange = hasCapability(session.data, 'hr.onboarding_template:change');
  const templates = useTemplates();
  const [editing, setEditing] = useState<OnboardingTemplateDto | 'new' | null>(null);
  const [toggling, setToggling] = useState<OnboardingTemplateDto | null>(null);
  const rows = templates.data ?? [];
  return (
    <Stack spacing={3}>
      <PageHeader
        title="Onboarding templates"
        description="The tasks an onboarding checklist starts with, who does each and when."
        actions={
          canCreate ? (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={(event) => {
                event.currentTarget.focus();
                setEditing('new');
              }}
            >
              New template
            </Button>
          ) : undefined
        }
      />
      {templates.isError && <ErrorState message={templates.error.message} />}
      {templates.isPending && <LoadingState label="Loading onboarding templates" />}
      {templates.data !== undefined && rows.length === 0 && (
        <Section>
          <EmptyState
            title="No onboarding templates yet"
            {...(canCreate
              ? { detail: 'Create one with the tasks every new employee needs.' }
              : {})}
          />
        </Section>
      )}
      {rows.map((template) => (
        <Section
          key={template.id}
          title={template.name}
          actions={
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <StatusChip status={template.status} />
              {canChange && (
                <>
                  <Button
                    size="small"
                    aria-label={`Edit ${template.name}`}
                    onClick={(event) => {
                      event.currentTarget.focus();
                      setEditing(template);
                    }}
                  >
                    Edit
                  </Button>
                  <Button
                    size="small"
                    color={template.status === 'active' ? 'warning' : 'primary'}
                    aria-label={`${template.status === 'active' ? 'Deactivate' : 'Activate'} ${template.name}`}
                    onClick={(event) => {
                      event.currentTarget.focus();
                      setToggling(template);
                    }}
                  >
                    {template.status === 'active' ? 'Deactivate' : 'Activate'}
                  </Button>
                </>
              )}
            </Stack>
          }
        >
          <Table size="small" aria-label={`Tasks of ${template.name}`}>
            <TableHead>
              <TableRow>
                <TableCell>Task</TableCell>
                <TableCell>Responsible</TableCell>
                <TableCell>Due</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {template.tasks.map((task, index) => (
                <TableRow key={`${String(index)}-${task.title}`}>
                  <TableCell>{task.title}</TableCell>
                  <TableCell>
                    {task.responsibleName === null ? (
                      '—'
                    ) : (
                      <PersonName
                        name={task.responsibleName}
                        employeeId={task.responsibleEmployeeId}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    {task.dueDays === 0
                      ? 'On the joining date'
                      : `${String(task.dueDays)} ${task.dueDays === 1 ? 'day' : 'days'} after joining`}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Section>
      ))}
      {editing !== null && (
        <TemplateDialog
          {...(editing === 'new' ? {} : { template: editing })}
          onClose={() => {
            setEditing(null);
          }}
        />
      )}
      {toggling !== null && (
        <ToggleTemplate
          template={toggling}
          onClose={() => {
            setToggling(null);
          }}
        />
      )}
    </Stack>
  );
}

interface TaskDraft {
  readonly key: number;
  readonly title: string;
  readonly responsibleEmployeeId: string;
  readonly dueDays: string;
}

function TemplateDialog({
  template,
  onClose,
}: {
  template?: OnboardingTemplateDto;
  onClose: () => void;
}) {
  const session = useSession();
  const queryClient = useQueryClient();
  const [name, setName] = useState(template?.name ?? '');
  const [tasks, setTasks] = useState<TaskDraft[]>(
    template?.tasks.map((task, index) => ({
      key: index,
      title: task.title,
      responsibleEmployeeId: task.responsibleEmployeeId,
      dueDays: String(task.dueDays),
    })) ?? [{ key: 0, title: '', responsibleEmployeeId: '', dueDays: '0' }],
  );
  const update = (key: number, change: Partial<TaskDraft>) => {
    setTasks((current) => current.map((t) => (t.key === key ? { ...t, ...change } : t)));
  };
  const valid =
    name.trim() !== '' &&
    tasks.length > 0 &&
    tasks.every(
      (t) =>
        t.title.trim() !== '' &&
        t.responsibleEmployeeId !== '' &&
        /^\d{1,3}$/.test(t.dueDays) &&
        Number(t.dueDays) <= 365,
    );
  const body = {
    name: name.trim(),
    tasks: tasks.map((t) => ({
      title: t.title.trim(),
      responsibleEmployeeId: t.responsibleEmployeeId,
      dueDays: Number(t.dueDays),
    })),
  };
  const save = useMutation({
    mutationFn: () =>
      template === undefined
        ? postJson(TEMPLATES, body, OnboardingTemplateSchema, session.data?.csrfToken ?? '')
        : patchJson(
            `${TEMPLATES}/${template.id}`,
            body,
            OnboardingTemplateSchema,
            session.data?.csrfToken ?? '',
            template.version,
          ),
    onSuccess: async () => {
      await invalidateOnboarding(queryClient);
      onClose();
    },
  });
  return (
    <FormDialog
      open
      title={template === undefined ? 'New onboarding template' : `Edit ${template.name}`}
      submitLabel={template === undefined ? 'Create template' : 'Save changes'}
      canSubmit={valid}
      submitting={save.isPending}
      error={save.isError ? messageOf(save.error) : null}
      onClose={onClose}
      onSubmit={() => {
        save.mutate();
      }}
    >
      <TextField
        required
        label="Name"
        value={name}
        helperText="E.g. Standard onboarding"
        onChange={(e) => {
          setName(e.target.value);
        }}
        slotProps={{ htmlInput: { maxLength: 100 } }}
      />
      {template !== undefined && (
        <Alert severity="info" variant="outlined">
          Changes apply to checklists started from now on; checklists already started keep their
          tasks.
        </Alert>
      )}
      {tasks.map((task, index) => (
        <Box
          key={task.key}
          sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 1.5 }}
          role="group"
          aria-label={`Task ${String(index + 1)}`}
        >
          <Stack spacing={1.5}>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <TextField
                required
                fullWidth
                size="small"
                label={`Task ${String(index + 1)}`}
                value={task.title}
                onChange={(e) => {
                  update(task.key, { title: e.target.value });
                }}
                slotProps={{ htmlInput: { maxLength: 200 } }}
              />
              <IconButton
                aria-label={`Remove task ${String(index + 1)}`}
                disabled={tasks.length === 1}
                onClick={() => {
                  setTasks((current) => current.filter((t) => t.key !== task.key));
                }}
              >
                <DeleteOutlinedIcon fontSize="small" />
              </IconButton>
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <Box sx={{ flex: 2 }}>
                <ResponsiblePicker
                  id={task.responsibleEmployeeId}
                  onChange={(id) => {
                    update(task.key, { responsibleEmployeeId: id });
                  }}
                />
              </Box>
              <TextField
                required
                size="small"
                label="Due (days after joining)"
                value={task.dueDays}
                error={!/^\d{1,3}$/.test(task.dueDays) || Number(task.dueDays) > 365}
                onChange={(e) => {
                  update(task.key, { dueDays: e.target.value.trim() });
                }}
                slotProps={{ htmlInput: { inputMode: 'numeric' } }}
                sx={{ flex: 1 }}
              />
            </Stack>
          </Stack>
        </Box>
      ))}
      <Box>
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={() => {
            setTasks((current) => [
              ...current,
              {
                key: Math.max(...current.map((t) => t.key), 0) + 1,
                title: '',
                responsibleEmployeeId: '',
                dueDays: '0',
              },
            ]);
          }}
        >
          Add a task
        </Button>
      </Box>
    </FormDialog>
  );
}

/** The employee picker for a task, loading the employee already chosen by id. */
function ResponsiblePicker({ id, onChange }: { id: string; onChange: (id: string) => void }) {
  const current = useFilterEmployee(id, true);
  const [choice, setChoice] = useState<EmployeeDto | null | undefined>(undefined);
  return (
    <EmployeePicker
      label="Responsible"
      required
      value={choice === undefined ? current : choice}
      onChange={(employee) => {
        setChoice(employee);
        onChange(employee?.id ?? '');
      }}
    />
  );
}

function ToggleTemplate({
  template,
  onClose,
}: {
  template: OnboardingTemplateDto;
  onClose: () => void;
}) {
  const session = useSession();
  const queryClient = useQueryClient();
  const next = template.status === 'active' ? 'inactive' : 'active';
  const change = useMutation({
    mutationFn: () =>
      patchJson(
        `${TEMPLATES}/${template.id}`,
        { status: next },
        OnboardingTemplateSchema,
        session.data?.csrfToken ?? '',
        template.version,
      ),
    onSuccess: async () => {
      await invalidateOnboarding(queryClient);
      onClose();
    },
  });
  return (
    <ConfirmDialog
      open
      title={`${next === 'inactive' ? 'Deactivate' : 'Activate'} ${template.name}?`}
      message={
        next === 'inactive'
          ? 'It can no longer start a checklist. Checklists already started keep their tasks.'
          : 'HR can start checklists from it again.'
      }
      confirmLabel={next === 'inactive' ? 'Deactivate' : 'Activate'}
      tone={next === 'inactive' ? 'warning' : 'primary'}
      busy={change.isPending}
      error={change.isError ? change.error.message : null}
      onCancel={onClose}
      onConfirm={() => {
        change.mutate();
      }}
    />
  );
}
